import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { getDaysInMonth } from 'date-fns';
import { toast } from 'react-toastify';

import BaseBox from '../_Common/BaseBox';
import {
  Body,
  GoalItem,
  GoalHeader,
  Title,
  PercentText,
  MoneyProgress,
  BarProgress,
  Progress,
} from './GoalsStyles';
import CustomCircularProgress from '../_Common/CustomCircularProgress';
import { formatPrice } from '../../../../../utils/functions';
import { ApplicationState } from '../../../../../store';
import { changeUser } from '../../../../../store/modules/user/actions';
import api from '../../../../../services/api';

interface Config {
  tradeInWeekend: boolean;
  goalOfMonth: number;
  currency: string;
}

interface GoalsInterface {
  config: Config;
  progressToday: number;
  progressWeek: number;
  progressMonth: number;
}

const initialState = {
  config: {
    tradeInWeekend: true,
    goalOfMonth: 0,
    currency: 'BRL',
    stopWin: 0,
    stopLoss: 0,
  },
  progressToday: 0,
  progressWeek: 0,
  progressMonth: 0,
};

export default function Goals() {
  const dispatch = useDispatch();
  const [goals, setGoals] = useState<GoalsInterface>(initialState);
  const [editOpen, setEditOpen] = useState(false);
  const [editGoal, setEditGoal] = useState(0);
  const [editStopWin, setEditStopWin] = useState(0);
  const [editStopLoss, setEditStopLoss] = useState(0);
  const [saving, setSaving] = useState(false);
  const userState = useSelector((state: ApplicationState) => state.user);
  const { loading, boxes } = useSelector(
    (state: ApplicationState) => state.boxes
  );

  useEffect(() => {
    if (boxes.Goals) {
      setGoals(boxes.Goals);
    }
  }, [boxes.Goals]);

  const theme = useTheme();

  function handleEditOpen() {
    setEditGoal(userState.user.config.goalOfMonth);
    setEditStopWin(userState.user.config.stopWin);
    setEditStopLoss(userState.user.config.stopLoss);
    setEditOpen(true);
  }

  function handleEditSave() {
    setSaving(true);
    api.put('/user', {
      goalOfMonth: editGoal,
      stopWin: editStopWin,
      stopLoss: editStopLoss,
    }).then(() => {
      dispatch(changeUser(
        userState.user.name,
        userState.user.phone,
        editGoal,
        userState.user.config.currency,
        editStopWin,
        editStopLoss,
      ));
      setSaving(false);
      setEditOpen(false);
      toast.success('Metas atualizadas!');
    }).catch(() => {
      setSaving(false);
      toast.error('Erro ao atualizar metas.');
    });
  }

  function goalBasedOnMonth(divider: number): number {
    return Math.round((userState.user.config.goalOfMonth / divider) * 1e2) / 1e2;
  }

  function percentCompleted(value: number, goal: number): number {
    return Math.round(((value * 100) / goal) * 1e1) / 1e1;
  }

  function absPercentCompleted(value: number, goal: number): number {
    const roundedValue = Math.round(((value * 100) / goal) * 1e1) / 1e1;
    if (roundedValue > 100 || roundedValue < -100) {
      return 100;
    }
    return Math.abs(Math.round(((value * 100) / goal) * 1e1) / 1e1);
  }

  const goalToday = goalBasedOnMonth(getDaysInMonth(new Date()));
  const goalWeek = goalBasedOnMonth(4);

  const absPercentDay =
    absPercentCompleted(goals.progressToday, goalToday) || 0;
  const absPercentWeek = absPercentCompleted(goals.progressWeek, goalWeek) || 0;
  const absPercentMonth =
    absPercentCompleted(goals.progressMonth, userState.user.config.goalOfMonth) || 0;

  const percentDay = percentCompleted(goals.progressToday, goalToday);
  const percentWeek = percentCompleted(goals.progressWeek, goalWeek);
  const percentMonth = percentCompleted(
    goals.progressMonth,
    userState.user.config.goalOfMonth
  );

  function getColor(value: number) {
    return value > 0 ? theme.colors.win : value < 0 ? theme.colors.loss : '#ffffff88';
  }

  const items = [
    { title: 'Diaria', value: goals.progressToday, goal: goalToday, absPercent: absPercentDay, percent: percentDay },
    { title: 'Semanal', value: goals.progressWeek, goal: goalWeek, absPercent: absPercentWeek, percent: percentWeek },
    { title: 'Mensal', value: goals.progressMonth, goal: userState.user.config.goalOfMonth, absPercent: absPercentMonth, percent: percentMonth },
  ];

  return (
    <BaseBox
      title="Metas"
      id="tour-box-goals"
      action={
        <IconButton size="small" onClick={handleEditOpen} style={{ color: '#ffffff88' }}>
          <EditIcon fontSize="small" />
        </IconButton>
      }
    >
      {!(loading && !boxes.Goals) && (
        <Body>
          {items.map((item, index) => (
            <GoalItem key={item.title}>
              <GoalHeader>
                <Title variant="body1">{item.title}</Title>
                <PercentText style={{ color: getColor(item.value) }}>
                  {item.percent}%
                </PercentText>
              </GoalHeader>
              <BarProgress>
                <Progress
                  value={item.absPercent}
                  achieved={item.absPercent >= 100}
                  color={item.value > 0 ? theme.colors.win : theme.colors.loss}
                />
              </BarProgress>
              <MoneyProgress variant="body2">
                <span style={{ color: getColor(item.value), fontWeight: 600 }}>
                  {formatPrice(item.value)}
                </span>
                {' de '}
                {formatPrice(item.goal)}
              </MoneyProgress>
            </GoalItem>
          ))}
        </Body>
      )}
      {loading && !boxes.Goals && <CustomCircularProgress />}

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        PaperProps={{
          style: {
            background: '#2C325C',
            color: '#fff',
            minWidth: '340px',
          }
        }}
      >
        <DialogTitle style={{ color: '#ffee58' }}>Editar metas</DialogTitle>
        <DialogContent>
          <TextField
            label="Meta mensal"
            type="number"
            value={editGoal}
            onChange={e => setEditGoal(parseFloat(e.target.value) || 0)}
            variant="filled"
            fullWidth
            style={{ marginBottom: '16px' }}
          />
          <TextField
            label="Stop Win (diário)"
            type="number"
            value={editStopWin}
            onChange={e => setEditStopWin(parseFloat(e.target.value) || 0)}
            variant="filled"
            fullWidth
            style={{ marginBottom: '16px' }}
          />
          <TextField
            label="Stop Loss (diário)"
            type="number"
            value={editStopLoss}
            onChange={e => setEditStopLoss(parseFloat(e.target.value) || 0)}
            variant="filled"
            fullWidth
          />
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button onClick={() => setEditOpen(false)} style={{ color: '#ffffffcc' }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleEditSave}
            disabled={saving}
            style={{ color: '#1E2141', fontWeight: 700 }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>
    </BaseBox>
  );
}
